<template>
    <!-- Global label scanner. Opens over whatever page the user is on, verifies
         the record, then closes and navigates. It replaces a dedicated route
         whose only outcomes were "navigate away" or "try again": that left a
         dead camera page in the back stack after every scan. -->
    <ForkModal
        id="scan-label-modal"
        ref="modal"
        dialog-class="modal-dialog-scrollable"
        modal-title="Trace a label"
        size="modal-md"
        :mobile-fullscreen="true"
        @shown="onShown"
        @close="onClosed"
    >
        <div class="modal-body scan-label">
            <p class="scan-label__hint text-muted mb-3">
                Scan the QR on any Forkto label: a pallet, an intake, or a product. Forkto verifies
                the record in this account, then opens it.
            </p>

            <div class="scan-label__feedback" aria-live="polite" aria-atomic="true">
                <div
                    v-if="resolving"
                    class="scan-label__status"
                    role="status"
                    data-cy="scan-label-resolving"
                >
                    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    Verifying this record…
                </div>
                <Notice v-else-if="errorMessage" warning data-cy="scan-label-error">
                    <span class="scan-label__error-text">
                        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                        {{ errorMessage }}
                    </span>
                    <button
                        type="button"
                        class="btn btn-sm forkto-secondary-action"
                        data-cy="scan-label-retry"
                        @click="startScanning()"
                    >
                        Scan again
                    </button>
                </Notice>
            </div>

            <div class="scan-label__frame">
                <TrackingQrScanner
                    ref="scanner"
                    :busy="resolving"
                    show-scan-frame
                    @decoded="identifyRecord"
                    @scan-error="onScanError"
                />
            </div>

            <ul class="scan-label__types" aria-label="Supported record types">
                <li>Meat</li>
                <li>Ingredient</li>
                <li>Packaging</li>
                <li>Product</li>
            </ul>

            <p class="scan-label__privacy text-muted small mb-0">
                <i class="fas fa-shield-alt" aria-hidden="true"></i>
                The camera view stays on this device. Only the label identifier is used to find the
                record.
            </p>
        </div>
    </ForkModal>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import ForkModal from "@/components/common/ForkModal.vue";
import Notice from "@/components/common/Notice.vue";
import TrackingQrScanner from "@/components/TrackingQrScanner.vue";
import { emitter } from "@/services/emitter";
import { SCAN_LABEL_OPEN, useUniversalRecordScan } from "@/composables/useUniversalRecordScan";

const router = useRouter();
const modal = ref(null);
const scanner = ref(null);

const { resolving, errorMessage, startScanning, stopScanning, reset, identifyRecord, onScanError } =
    useUniversalRecordScan(scanner, {
        // Close first so the destination page is what the back button returns
        // from, then navigate. Closing after push would flash the modal over
        // the new page.
        async onResolved(destination) {
            stopScanning();
            modal.value?.close();
            await router.push(destination);
        }
    });

function open() {
    reset();
    modal.value?.open();
}

// Camera starts only once the modal body is visible; a hidden <video> element
// cannot acquire a stream.
function onShown() {
    startScanning();
}

function onClosed() {
    stopScanning();
}

onMounted(() => emitter.on(SCAN_LABEL_OPEN, open));
onBeforeUnmount(() => {
    emitter.off(SCAN_LABEL_OPEN, open);
    stopScanning();
});

defineExpose({ open, identifyRecord });
</script>

<style scoped>
.scan-label__hint {
    font-size: 0.875rem;
}

.scan-label__feedback:empty {
    display: none;
}

.scan-label__feedback {
    margin-bottom: 0.75rem;
}

.scan-label__status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    color: var(--forkto-ink-2);
    font-size: 0.875rem;
    background: var(--forkto-sunk);
    border: 1px solid var(--forkto-line);
    border-radius: var(--forkto-radius-control);
}

.scan-label__frame {
    overflow: hidden;
    border-radius: var(--forkto-radius-surface);
    background: var(--forkto-ink-1);
}

.scan-label__types {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin: 0.875rem 0 0.75rem;
    padding: 0;
    list-style: none;
}

.scan-label__types li {
    padding: 0.125rem 0.5rem;
    color: var(--forkto-ink-3);
    font-size: 0.75rem;
    background: var(--forkto-sunk);
    border: 1px solid var(--forkto-line);
    border-radius: 999px;
}

.scan-label__error-text {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    text-align: left;
}

.scan-label__privacy {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
}
</style>
